import { useMemo } from 'react';
import { DataTable, type ColumnDef } from '@vnetwork/ui/data';
import type { WatchpointType } from '@/api/healthcheck';
import { fmtLatency } from '../shared';

/**
 * Context-aware rendering of an incident's probe snapshot (`alarm_event.details`,
 * a ProbeResult). Each monitor type carries different signal in `metadata`, so the
 * summary line and detail block adapt to the type — a TLS incident shows cert
 * expiry, a DNS one shows resolved records, an elastic one shows the aggregation
 * breakdown, protocol ones show status/latency.
 */
type Details = Record<string, unknown> | null | undefined;

function meta(details: Details): Record<string, unknown> {
  const m = (details as Record<string, unknown> | null)?.metadata;
  return m && typeof m === 'object' ? (m as Record<string, unknown>) : {};
}
function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
function str(v: unknown): string {
  return v == null ? '' : String(v);
}

/** One-line, type-aware summary for list rows. */
export function incidentSummary(type: WatchpointType, details: Details, matchedItems?: Record<string, unknown>[] | null): string {
  if (!details) return 'No probe detail captured.';
  const d = details as Record<string, unknown>;
  const err = str(d.error_message);
  if (err) return err;
  const m = meta(details);

  switch (type) {
    case 'tls': {
      const days = num(m.days_until_expiry);
      if (days != null) return `Certificate expires in ${days} day${days === 1 ? '' : 's'}`;
      return 'TLS check';
    }
    case 'dns': {
      const recs = Array.isArray(m.records) ? (m.records as unknown[]) : [];
      return recs.length ? `Resolved ${str(m.record_type) || 'record'}: ${recs.join(', ')}` : 'DNS check';
    }
    case 'elastic':
    case 'waap':
    case 'delivery': {
      // Count the violating subset when available; fall back to total groups scanned.
      const matched = Array.isArray(matchedItems) ? matchedItems.length : null;
      const total = matched ?? num(m.total_items);
      return total != null ? `${total} group${total === 1 ? '' : 's'} matched the threshold` : 'Data signal';
    }
    default: {
      const code = num(d.status_code);
      const lat = num(d.latency_ms);
      const parts: string[] = [];
      if (code != null) parts.push(`HTTP ${code}`);
      if (lat != null) parts.push(fmtLatency(lat));
      return parts.length ? parts.join(' · ') : d.is_up ? 'Healthy' : 'Down';
    }
  }
}

// ---- Detail block ---------------------------------------------------------

function Row({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'danger' | 'warning' }) {
  if (value === '' || value == null) return null;
  const color = tone === 'danger' ? 'var(--vn-danger-text)' : tone === 'warning' ? 'var(--vn-warning-text)' : undefined;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--vn-space-3)', padding: '6px 0', borderBottom: '1px solid var(--vn-border-subtle)' }}>
      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>
      <span style={{ textAlign: 'right', color, fontWeight: tone ? 600 : undefined, wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

type DataItem = Record<string, unknown>;

function DataItemsTable({ items }: { items: DataItem[] }) {
  // Columns = union of keys across items; put metric columns (value/count) last,
  // so the aggregation breakdown reads dimension → metric left-to-right.
  const columns = useMemo<ColumnDef<DataItem, unknown>[]>(() => {
    const keys = Array.from(new Set(items.flatMap((it) => Object.keys(it))));
    const metricKeys = keys.filter((k) => k === 'value' || k === 'count');
    const dimKeys = keys.filter((k) => k !== 'value' && k !== 'count');
    return [...dimKeys, ...metricKeys].map((k) => ({
      id: k,
      header: k,
      enableSorting: false,
      cell: ({ row }) => str(row.original[k]),
    }));
  }, [items]);

  if (!items.length) return null;
  return (
    <DataTable
      columns={columns}
      data={items.slice(0, 20)}
      getRowId={(_, i) => String(i)}
      dense
      emptyState="No breakdown captured."
    />
  );
}

/** Full type-specific detail block for the incident dialog. */
export function IncidentContext({
  type,
  details,
  matchedItems,
}: {
  type: WatchpointType;
  details: Details;
  /** Violating data_items subset (elastic/waap/delivery). Preferred over the full probe breakdown. */
  matchedItems?: Record<string, unknown>[] | null;
}) {
  if (!details) return <p style={{ color: 'var(--vn-fg-muted)', margin: 0 }}>No probe detail captured for this incident.</p>;
  const d = details as Record<string, unknown>;
  const m = meta(details);
  const err = str(d.error_message);

  if (type === 'tls') {
    const days = num(m.days_until_expiry);
    return (
      <div>
        <Row label="Days until expiry" value={days != null ? `${days} days` : '—'} tone={days != null && days <= 14 ? 'danger' : days != null && days <= 30 ? 'warning' : undefined} />
        <Row label="Expires" value={str(m.not_after)} />
        <Row label="Issuer" value={str(m.issuer)} />
        <Row label="Subject" value={str(m.subject)} />
        <Row label="Error" value={err} tone="danger" />
      </div>
    );
  }

  if (type === 'dns') {
    const recs = Array.isArray(m.records) ? (m.records as unknown[]).map(str) : [];
    return (
      <div>
        <Row label="Record type" value={str(m.record_type)} />
        <Row label="Nameserver" value={str(m.nameserver)} />
        <Row label="Resolved records" value={recs.length ? recs.join(', ') : '—'} />
        <Row label="Error" value={err} tone="danger" />
      </div>
    );
  }

  if (type === 'elastic' || type === 'waap' || type === 'delivery') {
    // Prefer the alarm's matched_items (the subset that actually violated the
    // condition); fall back to the full probe breakdown for older events that
    // predate matched_items capture.
    const matched = Array.isArray(matchedItems) ? matchedItems : [];
    const all = Array.isArray(m.data_items) ? (m.data_items as Record<string, unknown>[]) : [];
    const usingMatched = matched.length > 0;
    const items = usingMatched ? matched : all;
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        <Row label="Groups matched" value={str(usingMatched ? matched.length : m.total_items)} />
        {!usingMatched && all.length > 0 && <Row label="Groups scanned" value={str(m.total_items)} />}
        {type !== 'elastic' && <Row label="Dashboard" value={str(m.dashboard)} />}
        <DataItemsTable items={items} />
        <Row label="Error" value={err} tone="danger" />
      </div>
    );
  }

  // http | https | tcp
  return (
    <div>
      <Row label="Status code" value={d.status_code != null ? str(d.status_code) : '—'} tone={num(d.status_code) && num(d.status_code)! >= 400 ? 'danger' : undefined} />
      <Row label="Latency" value={d.latency_ms != null ? fmtLatency(num(d.latency_ms)) : '—'} />
      <Row label="Region" value={str(d.region)} />
      <Row label="Error" value={err} tone="danger" />
    </div>
  );
}
